import { Camera, Share2, HelpCircle } from 'lucide-react';
import FoldableSection from './FoldableSection';
import CalendarDayButtonSimulated from './CalendarDayButtonSimulated';
import testHigh1 from '../images/test-high-1.jpg';
import testMedium1 from '../images/test-medium-1.jpg';
import testLow1 from '../images/test-low-1.jpg';

/**
 * CalendarHelpMenu - Reusable calendar help menu with foldable sections
 * @param {string} defaultOpenTab - The title of the section to open by default (optional)
 */
export default function CalendarHelpMenu({ defaultOpenTab = null }) {
  return (
    <div className="space-y-0">
      {/* Getting Started Section */}
      <FoldableSection title="Getting Started" defaultOpen={defaultOpenTab === 'Getting Started'}>
        <div className="space-y-3">
          <div className="flex items-center justify-center gap-2 mb-1">
            <HelpCircle className="h-4 w-4 text-muted-foreground" />
            <p className="text-sm font-bold text-muted-foreground text-center">Get started in 3 easy steps:</p>
          </div>

          {/* Step 1: Add period dates */}
          <div className="rounded-xl bg-muted/50 p-3 relative">
            <div className="absolute top-3 left-3 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground text-sm font-bold">
              1
            </div>
            <div className="flex-1 text-center">
              <h3 className="text-sm font-semibold text-foreground mb-1">Add period dates</h3>
              <p className="text-xs text-foreground mb-2">Select a day and mark as period start</p>
              {/* Mock UI component - PeriodDayToggle button */}
              <div className="flex justify-center">
                <button className="flex flex-col items-center gap-1 rounded-xl border p-3 text-xs border-border hover:border-destructive/50 bg-background" style={{ width: '175px', height: '70px' }}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22a7 7 0 0 0 7-7c0-2-1-3.9-3-5.5s-3.5-4-4-6.5c-.5 2.5-2 4.9-4 6.5C6 11.1 5 13 5 15a7 7 0 0 0 7 7z"/></svg>
                  Period day
                </button>
              </div>
            </div>
          </div>

          {/* Step 2: Add tests */}
          <div className="rounded-xl bg-muted/50 p-3 relative">
            <div className="absolute top-3 left-3 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground text-sm font-bold">
              2
            </div>
            <div className="flex-1 text-center">
              <h3 className="text-sm font-semibold text-foreground mb-1">Add tests</h3>
              <p className="text-xs text-foreground mb-2">Scan ovulation tests as you take them</p>
              {/* Mock UI component - OvulationTestButton */}
              <div className="flex flex-col items-center justify-center">
                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/50 text-primary-foreground shadow-lg blur-window">
                  <Camera className="h-6 w-6" />
                </div>
                <span className="mt-1 rounded-full bg-background/20 px-3 py-0.5 text-xs font-medium text-foreground blur-window">
                  Add test result
                </span>
              </div>
            </div>
          </div>

          {/* Step 3: Share */}
          <div className="rounded-xl bg-muted/50 p-3 relative">
            <div className="absolute top-3 left-3 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground text-sm font-bold">
              3
            </div>
            <div className="flex-1 text-center">
              <h3 className="text-sm font-semibold text-foreground mb-1">Share with your partners</h3>
              <p className="text-xs text-foreground mb-2">Click share and type in their nametag</p>
              {/* Mock UI component - Calendar share button */}
              <div className="flex justify-center">
                <button className="flex items-center gap-1.5 rounded-full border border-border bg-purple-500/20 px-3 py-2 text-xs font-semibold text-foreground shadow-lg backdrop-blur-sm">
                  <Share2 className="h-3.5 w-3.5" />
                  Share
                </button>
              </div>
            </div>
          </div>
        </div>
      </FoldableSection>

      {/* Reading Ovulation Tests Section */}
      <FoldableSection title="Reading Ovulation Tests" defaultOpen={defaultOpenTab === 'Reading Ovulation Tests'}>
        <div className="space-y-4">
          <p className="text-sm">
            Ovulation tests detect luteinizing hormone (LH), which surges 24-36 hours before ovulation. Understanding your test results is key to timing conception.
          </p>

          <div className="space-y-3">
            <div className="rounded-lg border border-border p-3">
              <h4 className="text-sm font-semibold text-foreground mb-2">High Fertility Test</h4>
              <div className="flex justify-center mb-2">
                <img src={testHigh1} alt="High fertility test" className="w-full h-auto rounded-md" />
              </div>
              <p className="text-xs text-muted-foreground">
                A high test shows two dark lines of similar intensity. This indicates an LH surge, meaning ovulation is likely within 24-36 hours. This is your most fertile window.
              </p>
            </div>

            <div className="rounded-lg border border-border p-3">
              <h4 className="text-sm font-semibold text-foreground mb-2">Medium Fertility Test</h4>
              <div className="flex justify-center mb-2">
                <img src={testMedium1} alt="Medium fertility test" className="w-full h-auto rounded-md" />
              </div>
              <p className="text-xs text-muted-foreground">
                A medium test shows the test line getting darker but not yet equal to the control line. LH levels are rising, so continue testing daily.
              </p>
            </div>

            <div className="rounded-lg border border-border p-3">
              <h4 className="text-sm font-semibold text-foreground mb-2">Low Fertility Test</h4>
              <div className="flex justify-center mb-2">
                <img src={testLow1} alt="Low fertility test" className="w-full h-auto rounded-md" />
              </div>
              <p className="text-xs text-muted-foreground">
                A low test shows one dark control line and a faint or absent test line. LH levels are low, indicating ovulation is not imminent. Continue testing daily.
              </p>
            </div>
          </div>

          <div className="rounded-lg bg-blue-50 border border-blue-200 p-3">
            <h4 className="text-sm font-semibold text-blue-900 mb-1">What is LH?</h4>
            <p className="text-xs text-blue-800">
              Luteinizing hormone (LH) is produced by the pituitary gland. The LH surge triggers the release of an egg from the ovary (ovulation). Testing for LH helps you identify your most fertile days.
            </p>
          </div>
        </div>
      </FoldableSection>

      {/* Understanding Your Cycle Section */}
      <FoldableSection title="Understanding Your Cycle" defaultOpen={defaultOpenTab === 'Understanding Your Cycle'}>
        <div className="space-y-4">
          <p className="text-sm">
            Your menstrual cycle is controlled by several hormones that work together to prepare your body for potential pregnancy each month.
          </p>

          <div className="space-y-3">
            <div className="rounded-lg border border-border p-3">
              <h4 className="text-sm font-semibold text-foreground mb-1">Follicular Phase (Days 1-13)</h4>
              <p className="text-xs text-muted-foreground">
                Estrogen rises as follicles develop in the ovaries. The uterine lining thickens in preparation for a potential pregnancy.
              </p>
            </div>

            <div className="rounded-lg border border-border p-3">
              <h4 className="text-sm font-semibold text-foreground mb-1">Ovulation (Day 14)</h4>
              <p className="text-xs text-muted-foreground">
                The LH surge triggers the release of a mature egg from the ovary. This is your peak fertility window, lasting about 24 hours.
              </p>
            </div>

            <div className="rounded-lg border border-border p-3">
              <h4 className="text-sm font-semibold text-foreground mb-1">Luteal Phase (Days 15-28)</h4>
              <p className="text-xs text-muted-foreground">
                Progesterone rises to support the uterine lining. If pregnancy doesn't occur, hormone levels drop and menstruation begins.
              </p>
            </div>
          </div>

          <div className="rounded-lg bg-purple-50 border border-purple-200 p-3">
            <h4 className="text-sm font-semibold text-purple-900 mb-1">Key Hormones</h4>
            <ul className="text-xs text-purple-800 space-y-1">
              <li>• <strong>Estrogen:</strong> Builds uterine lining, signals egg development</li>
              <li>• <strong>LH:</strong> Triggers ovulation (what tests detect)</li>
              <li>• <strong>Progesterone:</strong> Supports pregnancy after ovulation</li>
              <li>• <strong>FSH:</strong> Stimulates follicle growth at cycle start</li>
            </ul>
          </div>
        </div>
      </FoldableSection>

      {/* Calendar Symbols Section */}
      <FoldableSection title="Calendar Symbols" defaultOpen={defaultOpenTab === 'Calendar Symbols'}>
        <div className="space-y-4">
          <p className="text-sm">
            The calendar uses different colors and symbols to help you quickly understand your cycle status at a glance.
          </p>

            {/* Test Once / day */}
            <div className="rounded-lg border border-border p-3">
              <div className="flex">
                <div className="w-[60px] flex-shrink-0">
                  <CalendarDayButtonSimulated
                    dayNum={13}
                    dayData={{ ipData: { cd: 11 } }}
                  />
                </div>
                <div className="ml-3 flex-1">
                  <h4 className="text-sm font-semibold text-foreground">Test Once / day</h4>
                  <p className="text-xs text-muted-foreground">On days when ovulation could happens but is not super likely its reccomended to test once per day.</p>
                </div>
              </div>
            </div>

            {/* Test Twice / day */}
            <div className="rounded-lg border border-border p-3">
              <div className="flex">
                <div className="w-[60px] flex-shrink-0">
                  <CalendarDayButtonSimulated
                    dayNum={14}
                    dayData={{ ipData: { cd: 14 } }}
                  />
                </div>
                <div className="ml-3 flex-1">
                  <h4 className="text-sm font-semibold text-foreground">Test Twice / day</h4>
                  <p className="text-xs text-muted-foreground">The days that ovulation is likely, test twice per day to catch your LH surge and know when you will release an egg. </p>
                </div>
              </div>
            </div>

         

            {/* Insemination Day */}
            <div className="rounded-lg border border-border p-3">
              <div className="flex">
                <div className="w-[60px] flex-shrink-0">
                  <CalendarDayButtonSimulated
                    dayNum={15}
                    dayData={{ ipData: { inseminationDay: true, cd: 15 } }}
                  />
                </div>
                <div className="ml-3 flex-1">
                  <h4 className="text-sm font-semibold text-foreground">Insemination Day</h4>
                  <p className="text-xs text-muted-foreground">Blue outer border - marked as insemination day. Good luck! 🍀✨</p>
                </div>
              </div>
            </div>

          {/* Predicted Ovulation */}
          <div className="space-y-3">
            <div className="rounded-lg border border-border p-3">
              <div className="flex">
                <div className="w-[60px] flex-shrink-0">
                  <CalendarDayButtonSimulated
                    dayNum={15}
                    dayData={{ ipData: { predictedOvulation: true, cd: 15 } }}
                  />
                </div>
                <div className="ml-3 flex-1">
                  <h4 className="text-sm font-semibold text-foreground">Predicted Ovulation</h4>
                  <p className="text-xs text-muted-foreground">Purple border - estimated ovulation day based on cycle history</p>
                </div>
              </div>
            </div>

            {/* Predicted Period */}
            <div className="rounded-lg border border-border p-3">
              <div className="flex">
                <div className="w-[60px] flex-shrink-0">
                  <CalendarDayButtonSimulated
                    dayNum={28}
                    dayData={{ ipData: { predictedPeriod: true, cd: 28 } }}
                  />
                </div>
                <div className="ml-3 flex-1">
                  <h4 className="text-sm font-semibold text-foreground">Predicted Period</h4>
                  <p className="text-xs text-muted-foreground">Red border - estimated period start day</p>
                </div>
              </div>
            </div>

            {/* Actual Ovulation */}
            <div className="rounded-lg border border-border p-3">
              <div className="flex">
                <div className="w-[60px] flex-shrink-0">
                  <CalendarDayButtonSimulated
                    dayNum={14}
                    dayData={{ ipData: { peak: true, cd: 14 } }}
                  />
                </div>
                <div className="ml-3 flex-1">
                  <h4 className="text-sm font-semibold text-foreground">Actual Ovulation (Peak Test)</h4>
                  <p className="text-xs text-muted-foreground">Purple background - confirmed by high test value</p>
                </div>
              </div>
            </div>

            {/* Test results */}
            <div className="rounded-lg border border-border p-3">
              <div className="flex">
                <div className="w-[60px] flex-shrink-0">
                  <CalendarDayButtonSimulated
                    dayNum={12}
                    dayData={{ ipData: { cd: 12 }, maxTestValue: 0.8 }}
                  />
                </div>
                <div className="ml-3 flex-1">
                  <h4 className="text-sm font-semibold text-foreground">Test Background</h4>
                  <p className="text-xs text-muted-foreground">Purple opacity - shows test value intensity (darker = higher LH)</p>
                </div>
              </div>
            </div>

          </div>
        </div>
      </FoldableSection>

      {/* How Predictions Are Made Section */}
      <FoldableSection title="How Predictions Are Made" defaultOpen={defaultOpenTab === 'How Predictions Are Made'}>
        <div className="space-y-4">
          <p className="text-sm">
            Calendar predictions are estimates based on your cycle history. They help you plan, but only tests can confirm ovulation.
          </p>

          <div className="rounded-lg bg-muted/50 border border-border p-3">
            <h4 className="text-sm font-semibold text-foreground mb-2">How It Works</h4>
            <ul className="text-xs text-muted-foreground space-y-1">
              <li>• Predictions are calculated for 14 days in the future</li>
              <li>• Assumes a 28-day cycle as a baseline</li>
              <li>• Adjusts based on your actual period start dates</li>
              <li>• Ovulation is estimated at day 14 of the cycle</li>
            </ul>
          </div>

          <div className="rounded-lg border border-border p-3">
            <h4 className="text-sm font-semibold text-foreground mb-2">Ovulation Statistics</h4>
            <p className="text-xs text-muted-foreground mb-2">
              Most women ovulate between days 10-18 of their cycle, but this varies widely:
            </p>
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <div className="w-20 text-xs text-muted-foreground">CD 10:</div>
                <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div className="h-full bg-purple-500" style={{ width: '4%' }}></div>
                </div>
                <div className="text-xs text-muted-foreground">~4.0%</div>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-20 text-xs text-muted-foreground">CD 11:</div>
                <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div className="h-full bg-purple-500" style={{ width: '7.5%' }}></div>
                </div>
                <div className="text-xs text-muted-foreground">~7.5%</div>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-20 text-xs text-muted-foreground">CD 12:</div>
                <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div className="h-full bg-purple-500" style={{ width: '13%' }}></div>
                </div>
                <div className="text-xs text-muted-foreground">~13.0%</div>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-20 text-xs text-muted-foreground">CD 13:</div>
                <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div className="h-full bg-purple-500" style={{ width: '14.5%' }}></div>
                </div>
                <div className="text-xs text-muted-foreground">~14.5%</div>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-20 text-xs text-muted-foreground">CD 14:</div>
                <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div className="h-full bg-purple-500" style={{ width: '15.5%' }}></div>
                </div>
                <div className="text-xs text-muted-foreground">~15.5%</div>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-20 text-xs text-muted-foreground">CD 15:</div>
                <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div className="h-full bg-purple-500" style={{ width: '14%' }}></div>
                </div>
                <div className="text-xs text-muted-foreground">~14.0%</div>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-20 text-xs text-muted-foreground">CD 16:</div>
                <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div className="h-full bg-purple-500" style={{ width: '11.5%' }}></div>
                </div>
                <div className="text-xs text-muted-foreground">~11.5%</div>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-20 text-xs text-muted-foreground">CD 17:</div>
                <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div className="h-full bg-purple-500" style={{ width: '8%' }}></div>
                </div>
                <div className="text-xs text-muted-foreground">~8.0%</div>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-20 text-xs text-muted-foreground">CD 18:</div>
                <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div className="h-full bg-purple-500" style={{ width: '5%' }}></div>
                </div>
                <div className="text-xs text-muted-foreground">~5.0%</div>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              *CD 14 is the population median. These are general statistics. Your cycle may vary significantly.
            </p>
            <p className="text-xs text-muted-foreground mt-2">
              Source: <a href="https://pubmed.ncbi.nlm.nih.gov/11082086/" target="_blank" rel="noopener noreferrer" className="text-blue-500 underline">NIH National Institute of Health Study</a>
            </p>
          </div>

          <div className="rounded-lg bg-yellow-50 border border-yellow-200 p-3">
            <h4 className="text-sm font-semibold text-yellow-900 mb-1">Important Note</h4>
            <p className="text-xs text-yellow-800">
              <strong>Taking ovulation tests is the only way to actually know when you're ovulating.</strong> Predictions are estimates based on averages, but every woman's cycle is unique. Trust your test results over predictions.
            </p>
          </div>
        </div>
      </FoldableSection>
    </div>
  );
}
